import React, { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles, TextField, Button } from '@material-ui/core';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { FaWallet, FaExchangeAlt, FaDollarSign, FaBitcoin } from 'react-icons/fa';
import { IconButton, Tooltip } from '@material-ui/core';
import { metamaskLogo } from './config';
import { ethers } from 'ethers';
import { futLogoIcon200 } from './config';
import SwapPage from './SwapPage';

const useStyles = makeStyles((theme) => ({
    swapDialog: {
        color: 'lightgray',
    },
    swapText: {
        fontFamily: "'Roboto Mono', monospace",
        fontSize: '14px',
    },
    inputStyle: {
        width: '180px'
    },
    row: {
        margin: '0px',
        padding: '0px', 
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    linkButtonStyle: {
        color: 'white',
        backgroundColor: 'transparent',
        border: 'none',
        padding: '2px 4px', // Further reduce padding
        margin: '0',
        textDecoration: 'none',
        cursor: 'pointer',
        fontSize: '12px',
        textTransform: 'none',
        lineHeight: '1', // Reduce line height
    },
    walletButton: {
        borderRadius: '20px',  // Rounded corners
        borderColor: 'white',
        borderWidth: '2px',
        borderStyle: 'solid',
        backgroundColor: '#D3D3D3', // Light gray background
        color: theme.palette.primary.main, // Text color using primary from theme
        marginRight: '20px',
        height: '30px', // Smaller height
        '&:hover': {
            backgroundColor: '#B0B0B0' // Darker gray on hover
        }
    },
    toolIconStyle: {
        margin: theme.spacing(0.0), // No external margins
        padding: '5px', // Reduce padding inside the IconButton
        color: 'rgba(255, 255, 255, 0.7)', // Makes the color less bright
        '&:hover': {
            color: 'rgba(255, 255, 255, 1.0)', // Slightly brighter on hover
        },
        '& svg': { // Assuming the FontAwesome icon renders as an <svg> element
            fontSize: '0.9em',
        },
    },
    stack: {
        position: 'relative',
        display: 'inline-block',
        width: '1em',
        height: '1em',
      },
    bitcoin: {
        position: 'absolute',
        width: '1em',
        height: '1em',
        left: '0.1em', // Adjust this value to move the icon horizontally
        top: '0.0em', // Adjust this value to move the icon vertically
    },
    exchange: {
        position: 'absolute',
        width: '1em',
        height: '1em',
        left: '0.1em', // Adjust this value to move the icon horizontally
    },
    linkButtonStyle: {
        fontSize: '10px',
      },
}));

function WalletButton({ onClick, label }) {
    const classes = useStyles();
  
    return (
      <Button
        onClick={onClick}
        className={classes.walletButton}
      >
        {label}
      </Button>
    );
}

// Using our improved SwapPage component that delivers ultra-low fees (~$0.32)
// instead of the older SwapDialog implementation


// Debug check for MetaMask/window.ethereum availability
console.log('MetaMask availability check (global):', {
    hasWindowEthereum: typeof window !== 'undefined' && !!window.ethereum,
    windowEthereumObject: typeof window !== 'undefined' ? window.ethereum : 'unavailable'
});

const WalletConnect = React.memo(({ activeNodeId, activeNodeName, tokenPriceParam, tokenUniswapVersion, ethPrice, e3dPrice, setUserAddress, isDesktopVersion = false }) => {
    console.log('WalletConnect rendering with:', { activeNodeId, activeNodeName });
    const [walletAddress, setWalletAddress] = useState('');
    const [provider, setProvider] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogOpenE3D, setDialogOpenE3D] = useState(false);
    const classes = useStyles();

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleOpenDialogE3D = () => {
        setDialogOpenE3D(true);
    };

  const handleCloseDialogE3D = () => {
        setDialogOpenE3D(false);
    };

    // Define requestAccount using useCallback to maintain function reference identity between renders
    const requestAccount = useCallback(async () => {
        console.log('requestAccount function called');
        
        if (window.ethereum) {
            console.log('window.ethereum is available');
            try {
                console.log('Requesting eth_requestAccounts...');
                const accounts = await window.ethereum.request({
                    method: 'eth_requestAccounts'
                });
                console.log('Accounts received:', accounts);
                
                setWalletAddress(accounts[0]);
                setUserAddress(accounts[0]);
                
                // Save to localStorage for persistence across page navigation
                localStorage.setItem('userAddress', accounts[0]);
                
                const provider1 = new ethers.providers.Web3Provider(window.ethereum);
                console.log('Provider created:', provider1);
                setProvider(provider1);
            } catch (error) {
                console.error("Failed to get wallet access", error);
            }
        } else {
            console.error('window.ethereum not found');
            alert('MetaMask is not installed. Please consider installing it: https://metamask.io/download.html');
        }
    }, [setUserAddress]); // Add dependencies to useCallback

    // Add a function to handle clicks with debugging
    const handleMetaMaskClick = (e) => {
        console.log('MetaMask click handler executed');
        // Prevent any default actions
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        // Call the requestAccount function
        requestAccount();
    };

    useEffect(() => {
        console.log('WalletConnect useEffect running, walletAddress:', walletAddress);
        const checkIfWalletIsConnected = async () => {
            // First check localStorage for saved wallet address
            const savedUserAddress = localStorage.getItem('userAddress');
            
            if (savedUserAddress) {
                console.log('WalletConnect - Restoring wallet address from localStorage:', savedUserAddress);
                setWalletAddress(savedUserAddress);
                setUserAddress(savedUserAddress);
                
                if (window.ethereum) {
                    const provider1 = new ethers.providers.Web3Provider(window.ethereum);
                    setProvider(provider1);
                }
            }
            
            // Then check if MetaMask is still connected (may have accounts not in localStorage)
            if (window.ethereum) {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    if (accounts.length > 0) {
                        setWalletAddress(accounts[0]);
                        setUserAddress(accounts[0]);
                        const provider1 = new ethers.providers.Web3Provider(window.ethereum);
                        console.log('WalletConnect - Provider created:', provider1);
                        setProvider(provider1);
                        
                        // Also save to localStorage
                        localStorage.setItem('userAddress', accounts[0]);
                    }
                } catch (error) {
                    console.error("Failed to retrieve wallet", error);
                }
            }
        };

        checkIfWalletIsConnected();
    }, [setUserAddress]);
    
    const disconnectWallet = () => {
        // Clear the wallet address state
        setWalletAddress(null);
        setUserAddress(null);
        
        // Clear the provider state if you store it
        setProvider(null);
      
        // Also remove from localStorage
        localStorage.removeItem('userAddress');
      
        // Note: Modern MetaMask versions don't allow direct modification of selectedAddress
        // We can only clear our application state, not the wallet's internal state
        
        // Clear any other state or side effects as needed
        console.log("Wallet disconnected. Application state and localStorage cleared.");
      
        // If you need to do any further clean-up, do it here
    };
 
    console.log('WalletConnect rendering, walletAddress:', walletAddress);
    
    return (
        <div>
            {walletAddress ? (
                <div style={{display: 'flex', paddingRight: isDesktopVersion ? '0px' : '10px'}}>

                    {tokenPriceParam && !isDesktopVersion && (
                        <div style={{display: 'flex', paddingRight: '10px'}}>
                            <Tooltip title="Swap ETH for current Token">
                                <IconButton enabled={tokenPriceParam} onClick={handleOpenDialog} className={classes.toolIconStyle}>
                                    <span className={classes.stack}>
                                        <FaBitcoin className={classes.bitcoin} style={{width:'25px', height:'25px', color: 'green'}}/>
                                        <FaExchangeAlt className={classes.exchange} />
                                    </span>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Swap ETH for E3D Token">
                                <IconButton enabled={tokenPriceParam} onClick={handleOpenDialogE3D} className={classes.toolIconStyle}>
                                    <span className={classes.stack}>
                                        <img src={futLogoIcon200} style={{width:'25px', height:'25px', color: 'white'}} alt="" className={classes.bitcoin}/>
                                        <FaExchangeAlt className={classes.exchange} />
                                    </span>
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}

                    <div style={{border: '1px solid green' }} >
                        <Tooltip title="Disconnect Wallet">
                            <IconButton onClick={disconnectWallet} className={classes.toolIconStyle}>
                                <img src={metamaskLogo} style={{width:'30px', height:'30px'}} alt="Connect Wallet"/> 
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <div>
                    {isDesktopVersion ? (
                        <Tooltip title="Connect Wallet">
                            <IconButton 
                                onClick={handleMetaMaskClick}
                                className={classes.toolIconStyle}
                                style={{
                                    padding: 0,
                                    transition: 'box-shadow 0.3s'
                                }}
                            >
                                <img 
                                    src={metamaskLogo} 
                                    style={{width:'30px', height:'30px'}} 
                                    alt="Connect Wallet"
                                />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        /* Mobile version with obvious button */
                        <button 
                            onClick={handleMetaMaskClick}
                            style={{
                                background: '#FF9500',
                                border: '2px solid #FF5500',
                                padding: '10px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 0 10px rgba(255, 149, 0, 0.7)'
                            }}
                        >
                            <img 
                                src={metamaskLogo} 
                                style={{width:'30px', height:'30px'}} 
                                alt="Connect Wallet"
                            />
                            <span style={{marginLeft: '8px', color: '#000', fontWeight: 'bold'}}>
                                Connect Wallet
                            </span>
                        </button>
                    )}
                </div>
            )}
            <SwapPage
                open={dialogOpen}
                onClose={handleCloseDialog}
                tokenName={activeNodeName}
                tokenAddress={activeNodeId}
                provider={provider}
                walletAddress={walletAddress}
                tokenUniswapVersion={tokenUniswapVersion}
                tokenPriceParam={tokenPriceParam}
                ethPrice={ethPrice}
                isDialog={true}
                isCurrentToken={true} // Flag to indicate this is the current token swap
            />
            <SwapPage
                open={dialogOpenE3D}
                onClose={handleCloseDialogE3D}
                tokenName={"E3DToken"}
                tokenAddress={"0x6488861b401F427D13B6619C77C297366bCf6386"}
                provider={provider}
                walletAddress={walletAddress}
                tokenPriceParam={e3dPrice}
                tokenUniswapVersion={tokenUniswapVersion}
                ethPrice={ethPrice}
                isDialog={true}
            />
        </div>
    );
}, (prevProps, nextProps) => {
    // Prevent unnecessary re-renders by comparing props
    console.log('WalletConnect memo check:', { prev: prevProps, next: nextProps });
    return (
        prevProps.activeNodeId === nextProps.activeNodeId &&
        prevProps.activeNodeName === nextProps.activeNodeName &&
        prevProps.tokenPriceParam === nextProps.tokenPriceParam &&
        prevProps.tokenUniswapVersion === nextProps.tokenUniswapVersion &&
        prevProps.ethPrice === nextProps.ethPrice &&
        prevProps.e3dPrice === nextProps.e3dPrice &&
        prevProps.isDesktopVersion === nextProps.isDesktopVersion
    );
});

export default WalletConnect;
